import { useState } from 'react';
import {
  genreOptions,
  regionOptions,
  yearOptions,
  sortOptions,
} from '@/mocks/filters';

export interface CategoryFilterState {
  genre: string;
  region: string;
  year: string;
  sort: string;
}

interface FilterBarProps {
  filters: CategoryFilterState;
  onChange: (patch: Partial<CategoryFilterState>) => void;
  onReset: () => void;
  resultCount: number;
}

function FilterSelect({
  label,
  value,
  options,
  onChange,
}: {
  label: string;
  value: string;
  options: { label: string; value: string }[];
  onChange: (value: string) => void;
}) {
  return (
    <label className="flex flex-1 flex-col gap-1.5 sm:flex-none">
      <span className="text-xs font-medium text-foreground-500">{label}</span>
      <select
        value={value}
        onChange={(event) => onChange(event.target.value)}
        className="h-10 w-full cursor-pointer rounded-md border border-background-300 bg-background-50 px-3 text-sm text-foreground-800 outline-none transition-colors hover:border-primary-300 focus:ring-2 focus:ring-primary-400/40 sm:w-36"
      >
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
    </label>
  );
}

export default function FilterBar({
  filters,
  onChange,
  onReset,
  resultCount,
}: FilterBarProps) {
  const [open, setOpen] = useState(false);

  const isDirty =
    filters.genre !== 'all' ||
    filters.region !== 'all' ||
    filters.year !== 'all' ||
    filters.sort !== 'update';

  return (
    <div className="rounded-lg border border-background-200 bg-background-50 p-4">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2 text-sm text-foreground-600">
          <i className="ri-filter-3-line text-primary-600"></i>
          共
          <span className="font-semibold text-primary-600">{resultCount}</span>
          部作品
        </div>
        <button
          type="button"
          onClick={() => setOpen((prev) => !prev)}
          className="flex cursor-pointer items-center gap-1 whitespace-nowrap rounded-md border border-background-300 px-3 py-1.5 text-sm text-foreground-700 sm:hidden"
        >
          筛选
          <i className={open ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'}></i>
        </button>
      </div>

      <div className={`${open ? 'mt-4 flex flex-col gap-4' : 'mt-4 hidden'} sm:flex sm:flex-row sm:flex-wrap sm:items-end sm:gap-4`}>
        <FilterSelect
          label="体裁"
          value={filters.genre}
          options={genreOptions}
          onChange={(value) => onChange({ genre: value })}
        />
        <FilterSelect
          label="地区"
          value={filters.region}
          options={regionOptions}
          onChange={(value) => onChange({ region: value })}
        />
        <FilterSelect
          label="年份"
          value={filters.year}
          options={yearOptions}
          onChange={(value) => onChange({ year: value })}
        />

        <div className="flex flex-col gap-1.5">
          <span className="text-xs font-medium text-foreground-500">排序</span>
          <div className="flex items-center gap-1 rounded-md bg-background-200 p-1">
            {sortOptions.map((option) => (
              <button
                key={option.value}
                type="button"
                onClick={() => onChange({ sort: option.value })}
                className={`cursor-pointer whitespace-nowrap rounded-md px-3 py-1.5 text-sm transition-colors ${
                  filters.sort === option.value
                    ? 'bg-background-50 font-medium text-primary-600'
                    : 'text-foreground-600 hover:text-foreground-900'
                }`}
              >
                {option.label.replace('按', '')}
              </button>
            ))}
          </div>
        </div>

        <div className="sm:ml-auto">
          <button
            type="button"
            onClick={onReset}
            disabled={!isDirty}
            className="flex h-10 w-full cursor-pointer items-center justify-center gap-1.5 whitespace-nowrap rounded-md border border-background-300 px-4 text-sm text-foreground-600 transition-colors hover:border-primary-300 hover:text-primary-600 disabled:cursor-not-allowed disabled:opacity-40 sm:w-auto"
          >
            <i className="ri-refresh-line"></i>
            重置筛选
          </button>
        </div>
      </div>
    </div>
  );
}